import { convertMany } from 'convert'
import { forecastMath } from 'features/math'
import {
  convertCoursesToPolygons,
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoCourseJSON,
  GeckoFeatureModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlateJSON,
  GeckoShellCourse,
  GeckoVerticalWeldJSON,
  generateManways_FastField,
  generateNozzles_FastField,
  generatePlatesFromVerticalWelds,
  WeldedPolygon,
  WrappingDirection,
} from 'types'
import { assetModelPrecision } from 'utils/constants'
import { v4 as uuidv4 } from 'uuid'

const { round } = forecastMath

export function getJsonFileParser(
  componentType: GeckoComponentModelType
): ((jsonFile: Record<string, unknown>) => Record<string, unknown>) | null {
  switch (componentType) {
    case GeckoComponentModelType.VerticalTankShell:
      return verticalTankShellFeaturesFromJSON
    case GeckoComponentModelType.TankFloor:
      return tankFloorFromJSON
    case GeckoComponentModelType.TankFlatRoof:
      return tankRoofFromJSON
    default:
      return null
  }
}

export function verticalTankShellFeaturesFromJSON(
  jsonFile: Record<string, unknown>
) {
  const tankCircumference = GeckoMeasurement.CreateFromValue(
    convertMany(jsonFile.circumference_measured as string).to('ft'),
    GeckoMeasurementUnit.Feet
  ).convertTo(GeckoMeasurementUnit.Meters)

  const tankHeight = GeckoMeasurement.CreateFromValue(
    convertMany(jsonFile.height_measured as string).to('ft'),
    GeckoMeasurementUnit.Feet
  ).convertTo(GeckoMeasurementUnit.Meters)

  const coursesArray: Array<GeckoCourseJSON> = new Array<GeckoCourseJSON>()
  const courseData = jsonFile.shell_course_weld_lines as Array<
    Record<string, number | Record<string, string>>
  >
  courseData.forEach((course) => {
    const courseFields = course.fields as Record<string, string>
    const yEnd =
      convertMany(courseFields.course_height).to('m') +
      convertMany(courseFields.elevation_start).to('m')
    coursesArray.push({
      name: courseFields.course,
      type: GeckoFeatureModelType.Course,
      feature_geometry: {
        y_end: round(yEnd, assetModelPrecision),
      },
      nominal: {},
    } as GeckoCourseJSON)
  })

  const coursesMap = new Map<string, GeckoCourseJSON>()
  coursesArray.forEach((course) => coursesMap.set(uuidv4().slice(0, 6), course))

  // Digest the vertical weld data from fastfield to form plates
  const verticalWelds: Array<GeckoVerticalWeldJSON> =
    new Array<GeckoVerticalWeldJSON>()
  const verticalWeldData = jsonFile.vertical_weld_lines as Array<
    Record<string, Record<string, string | number>>
  >
  verticalWeldData.forEach((ff_weld_section) => {
    if (coursesArray.length === 0) {
      throw Error(
        'Unable to generate vertical welds. Course data is undefined on component!'
      )
    }
    const courseNumber = ff_weld_section.fields.vwl_course as number
    const matchedCourseJSONs = coursesArray.filter((course) => {
      return course.name === String(courseNumber)
    })

    if (matchedCourseJSONs.length !== 1) {
      throw Error(
        'Unable to generate an asset model using this fastfield data! Unable to find the matching course for weld lines!'
      )
    }

    const matchedCourse = GeckoShellCourse.CreateFromJSON(
      'pseudo-component-id',
      'pseudo-course-id',
      matchedCourseJSONs[0]
    )

    const previousCourseJSONs = coursesArray.filter((course) => {
      return course.name === String(courseNumber - 1)
    })
    let previousCourse: GeckoShellCourse | undefined
    if (previousCourseJSONs.length > 1) {
      throw Error(
        'Unexpected state! Multiple courses matched when looking for previous course.'
      )
    } else if (previousCourseJSONs.length === 1) {
      previousCourse = GeckoShellCourse.CreateFromJSON(
        'pseudo-component-id',
        'pseudo-course-id',
        previousCourseJSONs[0]
      )
    }

    Object.keys(ff_weld_section.fields).forEach((key) => {
      if (key.startsWith('station_')) {
        const weldValue = ff_weld_section.fields[key] as string
        const weldXPos = convertMany(weldValue).to('m')
        const yStart = previousCourse ? previousCourse.geometry.y.max.num : 0
        const yEnd = matchedCourse.geometry.y.max.num
        verticalWelds.push({
          type: GeckoFeatureModelType.VerticalWeld,
          feature_geometry: {
            y_start: round(yStart, 5),
            y_end: round(yEnd, 5),
            x: round(weldXPos, 5),
          },
          name: undefined,
        })
      }
    })
  })

  const tankPlates = generatePlatesFromVerticalWelds(
    verticalWelds,
    coursesMap,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters,
    tankCircumference
  )

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    '',
    0,
    0,
    tankCircumference.num,
    tankHeight.num,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )

  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertCoursesToPolygons(
    [...coursesMap.values()],
    componentBounds,
    polygonArray
  )
  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const tankNozzles = generateNozzles_FastField(
    jsonFile.nozzles as Array<Record<string, number | Record<string, string>>>
  )

  const tankManways = generateManways_FastField(
    jsonFile.landmark as Array<Record<string, number | Record<string, string>>>
  )

  const featureSets = []
  if (coursesMap) featureSets.push(coursesMap)
  if (tankPlates) featureSets.push(tankPlates)
  if (welds) featureSets.push(welds)
  if (tankNozzles) featureSets.push(tankNozzles)
  if (tankManways) featureSets.push(tankManways)

  const features = new Map<string, unknown>()
  for (const featureSet of featureSets) {
    for (const [featureID, feature] of featureSet) {
      features.set(featureID, feature)
    }
  }

  return Object.fromEntries(features) as Record<string, unknown>
}

export function tankFloorFromJSON(jsonFile: Record<string, unknown>) {
  if (!jsonFile.diameter) {
    throw Error(
      'Unable to generate Tank Floor model. Diameter is missing from the JSON'
    )
  }
  const tankFloorDiameter = GeckoMeasurement.CreateFromValue(
    convertMany(jsonFile.diameter as string).to('ft'),
    GeckoMeasurementUnit.Feet
  )

  const longSeamWelds = (jsonFile.long_seam_welds || []) as Array<string>
  const shortSeamWelds = (jsonFile.short_seam_welds || []) as Array<
    Record<string, string>
  >

  const tankPlates = new Map<string, GeckoPlateJSON>()

  let plateName = 0

  let plateNamingFromTop = true

  const longSeamOrientation = jsonFile.long_seam_orientation as
    | 'vertical'
    | 'horizontal'

  // All this does is flip where the naming of the plates beings (left to right, or right to left)
  const reverseOrientation = jsonFile.reverse_plate_order
  if (reverseOrientation) {
    longSeamWelds.reverse()
    shortSeamWelds.reverse()
  }

  shortSeamWelds.forEach((shortWeldRegion, _index) => {
    if (Object.keys(shortWeldRegion).length === 0) {
      return
    }
    // If reverse Orientation is true, the longWelds start from the end, so the start of the weld is the next weld in the array
    const longWeldStart = reverseOrientation
      ? convertMany(longSeamWelds[_index + 1] as string).to('ft')
      : convertMany(longSeamWelds[_index] as string).to('ft')
    const longWeldEnd = reverseOrientation
      ? convertMany(longSeamWelds[_index] as string).to('ft')
      : convertMany(longSeamWelds[_index + 1] as string).to('ft')

    // Append an artifical start and end weld to the shortWeldRegion
    shortWeldRegion['short_weld_start'] = "0'"
    shortWeldRegion['short_weld_end'] = `${tankFloorDiameter.num}'`
    const sortedWelds = Object.keys(shortWeldRegion).sort((a, b) => {
      return (
        // if snakeNamePlate is true, sort the keys in descending order, else sort in ascending order
        plateNamingFromTop
          ? convertMany(shortWeldRegion[b]).to('ft') -
              convertMany(shortWeldRegion[a]).to('ft')
          : convertMany(shortWeldRegion[a]).to('ft') -
              convertMany(shortWeldRegion[b]).to('ft')
      )
    })
    for (let i = 0; i < sortedWelds.length - 1; i++) {
      plateName++
      const shortWeldEnd = plateNamingFromTop
        ? convertMany(shortWeldRegion[sortedWelds[i]]).to('ft')
        : convertMany(shortWeldRegion[sortedWelds[i + 1]]).to('ft')
      const shortWeldStart = plateNamingFromTop
        ? convertMany(shortWeldRegion[sortedWelds[i + 1]]).to('ft')
        : convertMany(shortWeldRegion[sortedWelds[i]]).to('ft')

      const plate: GeckoPlateJSON = {
        type: GeckoFeatureModelType.Plate,
        name: `P-${plateName}`,
        feature_geometry:
          longSeamOrientation === 'vertical'
            ? {
                x_start: GeckoMeasurement.CreateFromValue(
                  longWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_start: GeckoMeasurement.CreateFromValue(
                  shortWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                x_end: GeckoMeasurement.CreateFromValue(
                  longWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_end: GeckoMeasurement.CreateFromValue(
                  shortWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
              }
            : {
                x_start: GeckoMeasurement.CreateFromValue(
                  shortWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_start: GeckoMeasurement.CreateFromValue(
                  longWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                x_end: GeckoMeasurement.CreateFromValue(
                  shortWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_end: GeckoMeasurement.CreateFromValue(
                  longWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
              },
        surface_specs: {
          wall_nominal_thickness: undefined,
          coating_nominal_thickness: undefined,
        },
        installation_date: undefined,
      }
      tankPlates.set(String(uuidv4()), plate)
    }
    // Flip the plateNamingFromTop boolean so that the next set of plates between long welds start from the opposite end
    plateNamingFromTop = !plateNamingFromTop
  })

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    '',
    0,
    0,
    tankFloorDiameter.num,
    tankFloorDiameter.num,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )
  const featureSets = []
  if (tankPlates) featureSets.push(tankPlates)
  if (welds) featureSets.push(welds)

  const features = new Map<string, unknown>()
  for (const featureSet of featureSets) {
    for (const [featureID, feature] of featureSet) {
      features.set(featureID, feature)
    }
  }

  return Object.fromEntries(features) as Record<string, unknown>
}

export function tankRoofFromJSON(jsonFile: Record<string, unknown>) {
  if (!jsonFile.diameter) {
    throw Error(
      'Unable to generate Tank Roof model. Diameter is missing from the JSON'
    )
  }
  const tankRoofDiameter = GeckoMeasurement.CreateFromValue(
    convertMany(jsonFile.diameter as string).to('ft'),
    GeckoMeasurementUnit.Feet
  )

  const longSeamWelds = (jsonFile.long_seam_welds || []) as Array<string>
  const shortSeamWelds = (jsonFile.short_seam_welds || []) as Array<
    Record<string, string>
  >

  const tankPlates = new Map<string, GeckoPlateJSON>()

  let plateName = 0

  // This is used to determine between two long welds, whether the plate naming should start from the top or bottom
  let plateNamingFromTop = true

  const longSeamOrientation = jsonFile.long_seam_orientation as
    | 'vertical'
    | 'horizontal'

  // All this does is flip where the naming of the plates beings (left to right, or right to left)
  const reverseOrientation = jsonFile.reverse_plate_order
  if (reverseOrientation) {
    longSeamWelds.reverse()
    shortSeamWelds.reverse()
  }

  shortSeamWelds.forEach((shortWeldRegion, _index) => {
    if (Object.keys(shortWeldRegion).length === 0) {
      return
    }
    // If reverseOrientation is true, the longWelds start from the end, so the start of the weld is the next weld in the array
    const longWeldStart = reverseOrientation
      ? convertMany(longSeamWelds[_index + 1] as string).to('ft')
      : convertMany(longSeamWelds[_index] as string).to('ft')
    const longWeldEnd = reverseOrientation
      ? convertMany(longSeamWelds[_index] as string).to('ft')
      : convertMany(longSeamWelds[_index + 1] as string).to('ft')

    // Append an artifical start and end weld to the shortWeldRegion
    shortWeldRegion['short_weld_start'] = "0'"
    shortWeldRegion['short_weld_end'] = `${tankRoofDiameter.num}'`
    const sortedWelds = Object.keys(shortWeldRegion).sort((a, b) => {
      return (
        // sort the keys in descending order, else sort in ascending order
        plateNamingFromTop
          ? convertMany(shortWeldRegion[b]).to('ft') -
              convertMany(shortWeldRegion[a]).to('ft')
          : convertMany(shortWeldRegion[a]).to('ft') -
              convertMany(shortWeldRegion[b]).to('ft')
      )
    })
    for (let i = 0; i < sortedWelds.length - 1; i++) {
      plateName++
      const shortWeldEnd = plateNamingFromTop
        ? convertMany(shortWeldRegion[sortedWelds[i]]).to('ft')
        : convertMany(shortWeldRegion[sortedWelds[i + 1]]).to('ft')
      const shortWeldStart = plateNamingFromTop
        ? convertMany(shortWeldRegion[sortedWelds[i + 1]]).to('ft')
        : convertMany(shortWeldRegion[sortedWelds[i]]).to('ft')

      const plate: GeckoPlateJSON = {
        type: GeckoFeatureModelType.Plate,
        name: `P-${plateName}`,
        feature_geometry:
          longSeamOrientation === 'vertical'
            ? {
                x_start: GeckoMeasurement.CreateFromValue(
                  longWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_start: GeckoMeasurement.CreateFromValue(
                  shortWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                x_end: GeckoMeasurement.CreateFromValue(
                  longWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_end: GeckoMeasurement.CreateFromValue(
                  shortWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
              }
            : {
                x_start: GeckoMeasurement.CreateFromValue(
                  shortWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_start: GeckoMeasurement.CreateFromValue(
                  longWeldStart,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                x_end: GeckoMeasurement.CreateFromValue(
                  shortWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
                y_end: GeckoMeasurement.CreateFromValue(
                  longWeldEnd,
                  GeckoMeasurementUnit.Feet
                ).convertTo(GeckoMeasurementUnit.Meters).num,
              },
        surface_specs: {
          wall_nominal_thickness: undefined,
          coating_nominal_thickness: undefined,
        },
        installation_date: undefined,
      }
      tankPlates.set(String(uuidv4()), plate)
    }
    // Flip the plateNamingFromTop boolean so that the next set of plates between long welds start from the opposite end
    plateNamingFromTop = !plateNamingFromTop
  })

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    '',
    0,
    0,
    tankRoofDiameter.num,
    tankRoofDiameter.num,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )
  const featureSets = []
  if (tankPlates) featureSets.push(tankPlates)
  if (welds) featureSets.push(welds)

  const features = new Map<string, unknown>()
  for (const featureSet of featureSets) {
    for (const [featureID, feature] of featureSet) {
      features.set(featureID, feature)
    }
  }

  return Object.fromEntries(features) as Record<string, unknown>
}
